// ======================================
// Admin Services Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { servicesAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminServices() {
  if (!requireAuth(['admin', 'manager'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <div class="admin-toolbar" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-4);">
            <div class="filter-group">
                <!-- Search or Filter could go here -->
            </div>
            <button class="btn btn-primary btn-sm" id="add-service-btn">
              ${icons.plus || '+'} Add Service
            </button>
          </div>
          
          <div class="admin-card">
            <div class="admin-table-container">
              <table class="admin-table" id="services-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Price Range</th>
                    <th>Timeline</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody id="services-body">
                  <tr><td colspan="5" class="loading-cell">Loading services...</td></tr>
                </tbody>
              </table>
            </div>
          </div>
    
    <!-- Service Modal -->
    <div class="modal-overlay" id="service-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:700px;">
        <div class="modal-header">
          <h2 class="modal-title" id="modal-title">Add New Service</h2>
          <button class="modal-close" data-close-modal>&times;</button>
        </div>
        <div class="modal-body" id="modal-body">
            <!-- Form injected here -->
        </div>
      </div>
    </div>
  `;

  return renderAdminLayout('services', 'Services Management', content);
}

export async function initAdminServices() {
  initAdminLayout();

  // Load Data
  loadServices();

  // Add Service Button
  const addBtn = document.getElementById('add-service-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => showServiceForm());
  }

  // Close Modals
  document.querySelectorAll('[data-close-modal]').forEach(btn => {
    btn.addEventListener('click', closeModal);
  });
}

async function loadServices() {
  const tbody = document.getElementById('services-body');
  if (!tbody) return;

  try {
    const response = await servicesAPI.list();
    // Extract array from response object
    const services = response.data || [];

    if (!Array.isArray(services)) {
      console.error('Invalid services data:', services);
      throw new Error('API returned invalid data format');
    }

    if (services.length === 0) {
      tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:2rem;">No services found. Add one!</td></tr>';
      return;
    }

    tbody.innerHTML = services.map(s => `
      <tr>
        <td>
            <img src="${s.image || 'https://via.placeholder.com/50'}" alt="${s.title}" style="width:50px;height:32px;object-fit:cover;border-radius:4px;">
        </td>
        <td>
            <div style="font-weight:600;">${s.title}</div>
            <div style="font-size:12px;color:var(--color-text-muted);">${s.slug}</div>
        </td>
        <td>${s.price_range}</td>
        <td>${s.timeline}</td>
        <td>
          <div class="action-buttons">
            <button class="btn-icon" onclick="window.editService(${s.id})" title="Edit">${icons.edit || '✎'}</button>
            <button class="btn-icon delete" onclick="window.deleteService(${s.id})" title="Delete">${icons.trash || '🗑️'}</button>
            <a href="/services/${s.slug}" target="_blank" class="btn-icon" title="View">${icons.eye || '👁️'}</a>
          </div>
        </td>
      </tr>
    `).join('');
  } catch (error) {
    console.error('Failed to load services:', error);
    tbody.innerHTML = `<tr><td colspan="5" class="error-cell">Failed to load: ${error.message}</td></tr>`;
  }
}

function showServiceForm(service = null) {
  const modal = document.getElementById('service-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  const isEdit = !!service;
  modalTitle.textContent = isEdit ? 'Edit Service' : 'Add New Service';
  modal.style.display = 'flex';

  const highlightsValue = service?.highlights
    ? (Array.isArray(service.highlights) ? JSON.stringify(service.highlights, null, 2) : service.highlights)
    : '[\n  "Feature 1",\n  "Feature 2"\n]';

  modalBody.innerHTML = `
    <form id="service-form">
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Service Title *</label>
          <input type="text" name="title" class="form-input" required value="${service?.title || ''}">
        </div>
        <div class="form-group">
          <label class="form-label">Slug (URL) *</label>
          <input type="text" name="slug" class="form-input" required value="${service?.slug || ''}" ${isEdit ? 'readonly style="background:#f9f9f9;"' : ''}>
          <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px;">Unique URL identifier (a-z, 0-9, -)</div>
        </div>
        <div class="form-group">
          <label class="form-label">Price Range</label>
          <input type="text" name="price_range" class="form-input" value="${service?.price_range || ''}" placeholder="e.g. ₹5L - ₹10L">
        </div>
        <div class="form-group">
          <label class="form-label">Timeline</label>
          <input type="text" name="timeline" class="form-input" value="${service?.timeline || ''}" placeholder="e.g. 45-60 Days">
        </div>
        <div class="form-group full-width">
          <label class="form-label">Image URL</label>
          <input type="url" name="image" class="form-input" value="${service?.image || ''}" placeholder="https://...">
        </div>
        <div class="form-group full-width">
          <label class="form-label">Short Description</label>
          <textarea name="description" class="form-textarea" rows="2">${service?.description || ''}</textarea>
        </div>
        <div class="form-group full-width">
            <label class="form-label">Detailed Description (HTML allowed)</label>
            <textarea name="full_description" class="form-textarea" rows="6">${service?.full_description || ''}</textarea>
        </div>
        <div class="form-group full-width">
            <label class="form-label">Highlights (JSON Array)</label>
            <textarea name="highlights" class="form-textarea" rows="4" style="font-family:monospace;">${highlightsValue}</textarea>
        </div>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" onclick="window.closeModal()">Cancel</button>
        <button type="submit" class="btn btn-primary">${isEdit ? 'Update Service' : 'Create Service'}</button>
      </div>
    </form>
  `;

  // Auto-slug generation
  if (!isEdit) {
    const titleInput = modalBody.querySelector('input[name="title"]');
    const slugInput = modalBody.querySelector('input[name="slug"]');
    titleInput.addEventListener('input', (e) => {
      slugInput.value = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    });
  }

  // Submit Handler
  const form = document.getElementById('service-form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = 'Saving...';

    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    // Parse highlights
    try {
      if (data.highlights) {
        data.highlights = JSON.parse(data.highlights);
      } else {
        data.highlights = [];
      }
    } catch (err) {
      alert('Invalid JSON in Highlights field');
      btn.disabled = false;
      btn.textContent = isEdit ? 'Update Service' : 'Create Service';
      return;
    }

    try {
      if (isEdit) {
        await servicesAPI.update(service.id, data);
      } else {
        await servicesAPI.create(data);
      }
      closeModal();
      loadServices();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to save service');
      btn.disabled = false;
      btn.textContent = isEdit ? 'Update Service' : 'Create Service';
    }
  });
}

export function closeModal() {
  const modal = document.getElementById('service-modal');
  if (modal) modal.style.display = 'none';
}

// Global functions for inline usage
window.closeModal = closeModal;
window.editService = async (id) => {
  try {
    const service = await servicesAPI.get(id);
    showServiceForm(service);
  } catch (e) {
    alert('Failed to load service details');
  }
};
window.deleteService = async (id) => {
  if (confirm('Are you sure you want to delete this service? This cannot be undone.')) {
    try {
      await servicesAPI.delete(id);
      loadServices();
    } catch (e) {
      alert('Failed to delete service');
    }
  }
};
